import { useState } from 'react';
import { Timer } from '@repo/libs/Timer';
import { systemTimer } from '@repo/libs/SystemTimer';
import { useOnChange } from '../../../shared/hooks/useOnChange/useOnChange';
import { useTimeout } from '../../../shared/hooks/useTimeout/useTimeout';

// Faster work draws nothing, so a cached query or quick mutation never flashes.
const SHOW_DELAY_MS = 200;

// Once drawn, it lingers so work ending just past the delay can't strobe.
const LINGER_MS = 300;

type VisibilityState = 'hidden' | 'pending' | 'visible' | 'lingering';

const DELAY_MS: Record<VisibilityState, number | undefined> = {
  hidden: undefined,
  pending: SHOW_DELAY_MS,
  visible: undefined,
  lingering: LINGER_MS,
};

function nextVisibilityState({
  current,
  isActive,
}: {
  current: VisibilityState;
  isActive: boolean;
}): VisibilityState {
  if (isActive) {
    if (current === 'hidden') return 'pending';
    if (current === 'lingering') return 'visible';
    return current;
  }

  if (current === 'pending') return 'hidden';
  if (current === 'visible') return 'lingering';
  return current;
}

/**
 * Debounces a flickering flag: true once `isActive` holds for the show delay,
 * and for a beat after it drops.
 *
 * The `Timer` is injected into `useTimeout` (default `systemTimer`) so a spec
 * can pass a `FakeTimer`.
 */
export const useDelayedVisibility = ({
  isActive,
  timer = systemTimer,
}: {
  isActive: boolean;
  timer?: Timer;
}): boolean => {
  // Seeded through the transition so work active on first render starts the
  // show delay instead of sitting in 'hidden'.
  const [state, setState] = useState<VisibilityState>(function firstState() {
    return nextVisibilityState({ current: 'hidden', isActive });
  });

  useOnChange([isActive], function followIsActive() {
    setState(function toNextState(current) {
      return nextVisibilityState({ current, isActive });
    });
  });

  useTimeout(
    {
      callback: function onDelayElapsed() {
        setState(function toStateAfterWait(current) {
          return current === 'pending' ? 'visible' : 'hidden';
        });
      },
      delayMs: DELAY_MS[state],
    },
    timer,
  );

  return state === 'visible' || state === 'lingering';
};
